import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://giftedict-backend.onrender.com';

/**
 * GiftDiscoveryPanel - Admin tool for finding and suggesting gifts
 *
 * Features:
 * - AI-powered gift idea generation
 * - Manual gift entry
 * - Price comparison across retailers
 * - Send suggestions to users for review
 */

// Category icons for display
const CATEGORY_ICONS = {
  experience: '🎭',
  tech: '📱',
  fashion: '👗',
  home: '🏠',
  food: '🍫',
  wellness: '🧘',
  hobby: '🎨',
  personalized: '💝',
  default: '🎁'
};

// Retailer logos/colors
const RETAILER_STYLES = {
  amazon: { bg: 'bg-[#FF9900]', text: 'text-white', name: 'Amazon' },
  nordstrom: { bg: 'bg-black', text: 'text-white', name: 'Nordstrom' },
  etsy: { bg: 'bg-[#F56400]', text: 'text-white', name: 'Etsy' },
  target: { bg: 'bg-[#CC0000]', text: 'text-white', name: 'Target' },
  default: { bg: 'bg-slate-500', text: 'text-white', name: 'Store' }
};

// AI Generated Gift Card
function AIGiftCard({ idea, onSelect, isSelected, onEdit }) {
  const icon = CATEGORY_ICONS[idea.category] || CATEGORY_ICONS.default;

  return (
    <div
      className={`
        relative bg-white rounded-2xl border-2 p-5 transition-all duration-200 cursor-pointer
        ${isSelected
          ? 'border-indigo-500 shadow-lg shadow-indigo-500/20 scale-[1.02]'
          : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'
        }
      `}
      onClick={() => onSelect(idea)}
    >
      {/* Featured badge */}
      {idea.isFeatured && (
        <div className="absolute -top-2 -right-2 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold rounded-full shadow-lg">
          TOP PICK
        </div>
      )}

      {/* Selection indicator */}
      <div className={`
        absolute top-4 left-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
        ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}
      `}>
        {isSelected && (
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      <div className="pl-8">
        {/* Category & Name */}
        <div className="flex items-start gap-3 mb-3">
          <span className="text-2xl">{icon}</span>
          <div className="flex-1">
            <h4 className="font-bold text-slate-800 text-lg leading-tight">{idea.name}</h4>
            <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">{idea.category}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-slate-600 text-sm mb-3">{idea.description}</p>

        {/* Why it fits */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 mb-3">
          <p className="text-sm text-emerald-700">
            <span className="font-semibold">Why it's perfect:</span> {idea.whyItFits}
          </p>
        </div>

        {/* Price & Actions */}
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-slate-800">{idea.estimatedPrice || idea.price}</span>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(idea);
              }}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Search terms (for admin reference) */}
        {idea.searchTerms && (
          <p className="text-xs text-slate-400 mt-2 truncate">
            Search: {idea.searchTerms}
          </p>
        )}
      </div>
    </div>
  );
}

// Price Result Card
function PriceResultCard({ result, onSelect }) {
  const style = RETAILER_STYLES[result.retailer] || RETAILER_STYLES.default;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 ${style.bg} ${style.text} text-xs font-bold rounded`}>
            {style.name}
          </span>
          {result.isBestDeal && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded">
              BEST DEAL
            </span>
          )}
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-slate-800">${result.price.toFixed(2)}</p>
          {result.originalPrice && (
            <p className="text-sm text-slate-400 line-through">${result.originalPrice.toFixed(2)}</p>
          )}
        </div>
      </div>

      <h4 className="font-medium text-slate-800 mb-2">{result.name}</h4>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-slate-500">
          {result.rating && (
            <span className="flex items-center gap-1">
              <span className="text-amber-400">★</span> {result.rating}
            </span>
          )}
          {result.reviews && (
            <span>({result.reviews.toLocaleString()} reviews)</span>
          )}
        </div>
        <span className={result.inStock ? 'text-green-600' : 'text-red-600'}>
          {result.inStock ? 'In Stock' : 'Out of Stock'}
        </span>
      </div>

      <div className="flex gap-2 mt-3">
        <a
          href={result.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 py-2 text-center text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          View on {style.name}
        </a>
        <button
          onClick={() => onSelect(result)}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
        >
          Use This
        </button>
      </div>
    </div>
  );
}

// Manual Gift Entry Form
function ManualGiftForm({ initialData, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    store: '',
    link: '',
    category: 'personalized',
    whyItFits: '',
    isFeatured: false,
    ...initialData
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Gift Name *</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="e.g., Personalized Leather Journal"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="Brief description of the gift..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Price</label>
          <input
            type="text"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="$45"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Store</label>
          <input
            type="text"
            value={formData.store}
            onChange={(e) => setFormData(prev => ({ ...prev, store: e.target.value }))}
            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Amazon, Etsy, etc."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Product Link</label>
        <input
          type="url"
          value={formData.link}
          onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="https://..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="experience">Experience</option>
          <option value="tech">Tech & Gadgets</option>
          <option value="fashion">Fashion</option>
          <option value="home">Home & Decor</option>
          <option value="food">Food & Drink</option>
          <option value="wellness">Wellness</option>
          <option value="hobby">Hobby</option>
          <option value="personalized">Personalized</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Why It's Perfect</label>
        <textarea
          value={formData.whyItFits}
          onChange={(e) => setFormData(prev => ({ ...prev, whyItFits: e.target.value }))}
          className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows={2}
          placeholder="Explain why this gift is perfect for the recipient..."
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isFeatured"
          checked={formData.isFeatured}
          onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
          className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
        />
        <label htmlFor="isFeatured" className="text-sm text-slate-700">Mark as Top Pick</label>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
        >
          {initialData ? 'Update' : 'Add'} Gift
        </button>
      </div>
    </form>
  );
}

// Main Gift Discovery Panel
export function GiftDiscoveryPanel({ gift, onClose, onSuggestionsUpdated }) {
  const [activeTab, setActiveTab] = useState('ai'); // 'ai' | 'search' | 'manual'
  const [loading, setLoading] = useState(false);
  const [aiIdeas, setAiIdeas] = useState([]);
  const [selectedIdeas, setSelectedIdeas] = useState([]);
  const [priceResults, setPriceResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [existingSuggestions, setExistingSuggestions] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('authtoken');

  // Fetch existing suggestions on mount
  useEffect(() => {
    fetchExistingSuggestions();
  }, [gift.id]);

  const fetchExistingSuggestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/gifts/${gift.id}/suggestions`, {
        headers: { 'authtoken': token }
      });
      const data = await response.json();
      if (data.success) {
        setExistingSuggestions(data.data);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  };

  // Generate AI ideas
  const handleGenerateAI = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/generateGiftIdeas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authtoken': token
        },
        body: JSON.stringify({
          recipientName: gift.recipientName,
          relationship: gift.relationship,
          occasion: gift.occasion,
          interests: gift.interests,
          budget: gift.budget,
          comments: gift.comments
        })
      });

      const data = await response.json();

      if (data.success && data.ideas) {
        // Mark first as featured
        const ideasWithMeta = data.ideas.map((idea, idx) => ({
          ...idea,
          isFeatured: idx === 0,
          isAiGenerated: true
        }));
        setAiIdeas(ideasWithMeta);
        setSelectedIdeas([ideasWithMeta[0]]); // Auto-select the top pick
      } else {
        setError(data.error || 'Failed to generate ideas');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError('Failed to generate gift ideas. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Search prices
  const handleSearchPrices = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/searchPrices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authtoken': token
        },
        body: JSON.stringify({
          query: searchQuery,
          minPrice: null,
          maxPrice: null
        })
      });

      const data = await response.json();

      if (data.success) {
        setPriceResults(data.results);
      } else {
        setError(data.error || 'Failed to search prices');
      }
    } catch (err) {
      console.error('Price search error:', err);
      setError('Failed to search prices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle idea selection
  const handleToggleSelect = (idea) => {
    setSelectedIdeas(prev => {
      const isSelected = prev.some(i => i.id === idea.id || i.name === idea.name);
      if (isSelected) {
        return prev.filter(i => i.id !== idea.id && i.name !== idea.name);
      } else {
        return [...prev, idea];
      }
    });
  };

  // Add from price result
  const handleAddFromPrice = (result) => {
    const newIdea = {
      id: `price-${Date.now()}`,
      name: result.name,
      description: `From ${RETAILER_STYLES[result.retailer]?.name || result.retailer}`,
      price: `$${result.price.toFixed(2)}`,
      store: RETAILER_STYLES[result.retailer]?.name || result.retailer,
      link: result.url,
      category: 'default',
      whyItFits: '',
      isAiGenerated: false
    };

    setSelectedIdeas(prev => [...prev, newIdea]);
    setActiveTab('ai'); // Switch to AI tab to see selection
  };

  // Save manual gift
  const handleSaveManual = (data) => {
    const newIdea = {
      id: `manual-${Date.now()}`,
      ...data,
      isAiGenerated: false
    };

    if (editingIdea) {
      // Update existing
      setSelectedIdeas(prev => prev.map(i =>
        (i.id === editingIdea.id || i.name === editingIdea.name) ? { ...newIdea, id: editingIdea.id } : i
      ));
      setAiIdeas(prev => prev.map(i =>
        (i.id === editingIdea.id || i.name === editingIdea.name) ? { ...newIdea, id: editingIdea.id } : i
      ));
    } else {
      // Add new
      setSelectedIdeas(prev => [...prev, newIdea]);
    }

    setShowManualForm(false);
    setEditingIdea(null);
  };

  // Send suggestions to user
  const handleSendSuggestions = async () => {
    if (selectedIdeas.length === 0) return;

    setSending(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/gifts/${gift.id}/suggestions/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authtoken': token
        },
        body: JSON.stringify({
          suggestions: selectedIdeas.map((idea, idx) => ({
            name: idea.name,
            description: idea.description,
            price: idea.estimatedPrice || idea.price,
            store: idea.store,
            link: idea.link,
            imageUrl: idea.imageUrl,
            category: idea.category,
            whyItFits: idea.whyItFits,
            searchTerms: idea.searchTerms,
            isFeatured: idea.isFeatured || idx === 0
          }))
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuggestionsUpdated?.();
        onClose?.();
      } else {
        setError(data.error || 'Failed to send suggestions');
      }
    } catch (err) {
      console.error('Send suggestions error:', err);
      setError('Failed to send suggestions. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-2xl font-bold backdrop-blur-sm">
                {gift.recipientName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <h2 className="text-2xl font-bold">Find Gift for {gift.recipientName}</h2>
                <p className="text-white/80 text-sm">
                  {gift.occasion} • {gift.relationship} • Budget: {gift.budget || 'Not specified'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Recipient info pills */}
          {gift.interests && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-white/60 mr-1">Interests:</span>
              {gift.interests.split(',').map((interest, idx) => (
                <span key={idx} className="px-3 py-1 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                  {interest.trim()}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-slate-200 px-6">
          <div className="flex gap-1">
            {[
              { id: 'ai', label: 'AI Suggestions', icon: '✨' },
              { id: 'search', label: 'Price Search', icon: '🔍' },
              { id: 'manual', label: 'Add Manual', icon: '✏️' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'manual') setShowManualForm(true);
                }}
                className={`
                  px-5 py-3 text-sm font-medium border-b-2 transition-all
                  ${activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* AI Tab */}
          {activeTab === 'ai' && (
            <div>
              {/* Generate button */}
              {aiIdeas.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">✨</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">AI-Powered Gift Ideas</h3>
                  <p className="text-slate-500 mb-6 max-w-md mx-auto">
                    Let our AI analyze {gift.recipientName}'s profile and generate personalized gift suggestions
                  </p>
                  <button
                    onClick={handleGenerateAI}
                    disabled={loading}
                    className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:scale-105 active:scale-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Generating Ideas...
                      </span>
                    ) : (
                      'Generate Gift Ideas'
                    )}
                  </button>
                </div>
              )}

              {/* AI Ideas grid */}
              {aiIdeas.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-800">
                      {selectedIdeas.length} of {aiIdeas.length} ideas selected
                    </h3>
                    <button
                      onClick={handleGenerateAI}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {aiIdeas.map((idea, idx) => (
                      <AIGiftCard
                        key={idea.id || idx}
                        idea={idea}
                        isSelected={selectedIdeas.some(i => i.id === idea.id || i.name === idea.name)}
                        onSelect={handleToggleSelect}
                        onEdit={(idea) => {
                          setEditingIdea(idea);
                          setShowManualForm(true);
                          setActiveTab('manual');
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Tab */}
          {activeTab === 'search' && (
            <div>
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchPrices()}
                  placeholder="Search for a specific product..."
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleSearchPrices}
                  disabled={loading || !searchQuery.trim()}
                  className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {priceResults.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {priceResults.map((result, idx) => (
                    <PriceResultCard
                      key={result.id || idx}
                      result={result}
                      onSelect={handleAddFromPrice}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <span className="text-4xl mb-4 block">🔍</span>
                  Search for products to compare prices across retailers
                </div>
              )}
            </div>
          )}

          {/* Manual Tab */}
          {activeTab === 'manual' && showManualForm && (
            <div className="max-w-lg mx-auto">
              <ManualGiftForm
                initialData={editingIdea}
                onSave={handleSaveManual}
                onCancel={() => {
                  setShowManualForm(false);
                  setEditingIdea(null);
                  setActiveTab('ai');
                }}
              />
            </div>
          )}
        </div>

        {/* Footer with action */}
        <div className="bg-white border-t border-slate-200 p-4 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {existingSuggestions.length > 0 && (
              <span className="text-amber-600 font-medium">
                {existingSuggestions.length} suggestions already sent
              </span>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendSuggestions}
              disabled={selectedIdeas.length === 0 || sending}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-xl shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                `Send ${selectedIdeas.length} Suggestion${selectedIdeas.length !== 1 ? 's' : ''} to User`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiftDiscoveryPanel;
